import { useState, useCallback, useContext, useEffect } from "react";
import InputBase from "@mui/material/InputBase";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import debounce from "lodash/debounce";
import { Typography } from "@mui/material";
import Filters from "./Filters";
import { store } from "../../util/store";
import {
  ArrowUpwardRounded,
  Close,
  Search,
  TuneRounded,
} from "@mui/icons-material";

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    margin: "10px 26px",
  },
  searchRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
  },
  root: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: "white",
    alignItems: "center",
    flex: 1,
    borderRadius: "12px",
    boxShadow: "0px 1px 6px #bebebe50",
    border: "1px solid #0000001f",
    padding: "8px 4px 8px 12px",
  },
  searchIcon: {
    color: "#555",
    fontSize: "1.2rem",
    marginRight: "4px",
    flexShrink: 0,
  },
  input: {
    flex: "1 1 auto",
    padding: 0,
    "& input": {
      padding: 0,
      fontWeight: "600",
    },
    "& input::placeholder": {
      color: "#888",
      fontWeight: "normal",
      opacity: 1,
    },
  },
  clearButton: {
    padding: "6px",
  },
  filterButton: {
    backgroundColor: "white",
    borderRadius: "14px",
    boxShadow: "0px 1px 6px #bebebe50",
    border: "1px solid #0000001f",
    padding: "6px 12px",
    flexShrink: 0,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#333",
    gap: "5px",
  },
  filterPanel: {
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0px 1px 6px #bebebe50",
    border: "1px solid #0000001f",
    marginTop: "6px",
    overflow: "hidden",
  },
  showFilter: {
    maxHeight: "500px",
    transition: "max-height .4s ease-out",
  },
  hideFilter: {
    maxHeight: "0px",
    overflow: "hidden",
    transition: "max-height .3s ease-in",
  },
  collapseIcon: {
    display: "flex",
    margin: "0 auto",
  },
  filterLabel: {
    display: "flex",
    justifyContent: "center",
    margin: "6px",
    marginBottom: "4px",
    color: "gray",
  },
};

export const Searchbar = (props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { dispatch, searchTerm: globalSearchTerm } = useContext(store);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applySearchTerm = useCallback(
    debounce((value) => {
      dispatch({ type: "SET_SEARCH_TERM", payload: value });
    }, 500),
    [],
  );

  const handleChange = (event) => {
    event.stopPropagation();
    const val = event.target.value;
    setSearchTerm(val);
    applySearchTerm(val.trim());
  };

  const handleClear = () => {
    setSearchTerm("");
    applySearchTerm("");
  };

  useEffect(() => {
    setSearchTerm(globalSearchTerm ?? "");
  }, [globalSearchTerm]);

  const toggleFilters = () => {
    setFiltersOpen((prev) => !prev);
  };

  const hideFilters = () => {
    setFiltersOpen(false);
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.searchRow}>
        <div style={styles.root}>
          <Search sx={styles.searchIcon} />
          <InputBase
            placeholder="Where to?"
            sx={styles.input}
            onChange={handleChange}
            value={searchTerm}
            spellCheck={false}
          />
          {searchTerm.length > 0 && (
            <IconButton sx={styles.clearButton} onClick={handleClear}>
              <Close fontSize="small" />
            </IconButton>
          )}
        </div>
        <IconButton sx={styles.filterButton} onClick={toggleFilters}>
          <TuneRounded sx={{ fontSize: "1.2rem" }} />
          <Typography
            variant="caption"
            sx={{ fontWeight: 500, letterSpacing: "0.5px" }}
          >
            FILTERS
          </Typography>
        </IconButton>
      </div>
      <div style={filtersOpen ? styles.showFilter : styles.hideFilter}>
        <div style={styles.filterPanel}>
          <Typography variant="subtitle2" sx={styles.filterLabel}>
            Filter by...
          </Typography>
          <Filters />
          <IconButton onClick={hideFilters} sx={styles.collapseIcon}>
            <ArrowUpwardRounded />
          </IconButton>
        </div>
      </div>
    </div>
  );
};
